import { Layout } from "../components/Layout";
import { BackHome, SectionHeader, StatBlock, ProcessCard, FeatureCard, GreenCta } from "../components/Shared";
import { SeoText } from "../components/SeoText";
import { useSeo } from "../hooks/useSeo";
import {
  ArrowRight,
  MessageCircle,
  Mail,
  Calendar,
  FileText,
  Search,
  Bell,
  Moon,
  Send,
  MessageSquare,
  MessagesSquare,
} from "lucide-react";
import { Link } from "react-router-dom";
import { WHATSAPP_LINK } from "../lib/api";

const stats = [
  { value: "24/7", label: "Disponible" },
  { value: "<30s", label: "Temps de réponse" },
  { value: "0", label: "Application à installer" },
  { value: "100%", label: "Via WhatsApp" },
];

const capabilities = [
  { num: "01", Icon: Mail, title: "Rédige vos emails", text: "Dictez votre message en vocal — il rédige un email professionnel prêt à envoyer." },
  { num: "02", Icon: Calendar, title: "Gère votre agenda", text: "Ajoutez, modifiez ou consultez vos rendez-vous par simple message." },
  { num: "03", Icon: FileText, title: "Prépare vos réunions", text: "Résumé, ordre du jour, points clés — prêt en 30 secondes." },
  { num: "04", Icon: Search, title: "Répond à vos questions", text: "Infos sur vos clients, vos dossiers, vos tâches — accès immédiat." },
  { num: "05", Icon: Bell, title: "Rappels intelligents", text: "Définissez vos priorités du jour par vocal — il vous rappelle au bon moment." },
  { num: "06", Icon: Moon, title: "Disponible nuits et week-ends", text: "Votre assistant ne prend jamais de vacances." },
];

const WhatsApp = () => {
  useSeo({
    title: "Chatbot WhatsApp pour PME — Automatisation Genève",
    description: "Répondez instantanément aux messages WhatsApp de vos clients. Votre assistant IA traite les demandes courantes automatiquement, jour et nuit.",
    path: "/services/whatsapp-ia",
  });

  return (
    <Layout>
      <BackHome />

      <section className="px-6 pt-10 pb-12 md:pt-16 text-center">
        <div className="max-w-4xl mx-auto fade-in">
          <span className="pill-badge mb-5">💬 Assistant WhatsApp IA</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold title-text leading-[1.05] tracking-tight">
            Automatisez vos échanges WhatsApp <span className="accent-text">avec vos clients et prospects</span>
          </h1>
          <p className="body-text text-lg mt-7 leading-relaxed max-w-2xl mx-auto">
            Envoyez un message vocal ou texte — il s'occupe du reste.
          </p>
          <div className="flex flex-wrap gap-2 mt-7 mb-9 justify-center">
            <span className="pill-badge">🇨🇭 Données hébergées en Suisse</span>
            <span className="pill-badge">🔒 Conforme LPD / FADP</span>
            <span className="pill-badge">⚡ Mise en place en 24h</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 flex-wrap justify-center">
            <Link to="/contact" className="btn-primary" data-testid="wpp-demo-cta">
              Demander une démo gratuite <ArrowRight size={18} />
            </Link>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="btn-secondary">
              <MessageCircle size={18} /> WhatsApp
            </a>
          </div>
        </div>
      </section>

      <section className="px-6 pb-10">
        <div className="max-w-5xl mx-auto fade-in">
          <StatBlock stats={stats} testId="wpp-stats" />
        </div>
      </section>

      <section className="px-6 py-16 section-alt">
        <div className="max-w-6xl mx-auto">
          <SectionHeader
            badge="Capacités"
            title="Six capacités"
            accentTitle="essentielles."
            subtitle="Tout ce qu'un assistant humain peut faire — instantanément, depuis votre poche."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((c) => <FeatureCard key={c.num} {...c} />)}
          </div>
        </div>
      </section>

      <section className="px-6 py-16">
        <div className="max-w-6xl mx-auto">
          <SectionHeader badge="Le processus" title="Trois étapes." accentTitle="Aucun effort." />
          <div className="grid md:grid-cols-3 gap-6">
            <ProcessCard num="01" badge="Vocal ou texte" Icon={Send} title="Envoyez un message" text="Sur WhatsApp, comme à un collègue — vocal ou texte, c'est vous qui choisissez." />
            <ProcessCard num="02" badge="Instantané" Icon={MessageSquare} title="L'IA comprend & exécute" text="Elle interprète votre demande et lance l'action : email rédigé, RDV créé, info récupérée." />
            <ProcessCard num="03" badge="<30 secondes" Icon={MessagesSquare} title="Vous recevez le résultat" text="Réponse directement sur WhatsApp. Validez, ajustez ou envoyez en un tap." />
          </div>
        </div>
      </section>

      <GreenCta
        badge="Prochaine étape"
        title="Adoptez votre nouvel assistant en 24h."
        subtitle="Une démo gratuite. Vous verrez la magie en 15 minutes."
        primaryLabel={<>Demander une démo gratuite <ArrowRight size={18} /></>}
        primaryAction={() => (window.location.href = "/contact")}
        secondaryLabel="WhatsApp"
        secondaryAction={WHATSAPP_LINK}
        footnotes={["Démo gratuite", "Basé à Genève"]}
      />

      <SeoText>
        <h2 className="section-title mb-6">
          Un chatbot WhatsApp <span className="accent-text">pensé pour les PME suisses</span>
        </h2>
        <p className="body-text leading-relaxed mb-5">
          WhatsApp est devenu le canal de communication numéro un en Suisse pour les
          échanges entre PME et clients. Les gens ne veulent plus appeler, n'aiment plus
          les emails, et préfèrent le côté instantané du chat. Le problème : répondre à
          tous ces messages à la main est un puits sans fond. Mes équipes répondent vingt
          fois par jour aux mêmes questions, et le soir, le week-end, les clients
          attendent.
        </p>
        <p className="body-text leading-relaxed mb-5">
          Le chatbot WhatsApp IA prend en charge tout ce qui est répétitif :
          disponibilités, tarifs, horaires d'ouverture, suivi de commande, prise de
          rendez-vous, demande de devis, FAQ produit, demande d'adresse. Le client envoie
          son message comme à un collègue — texte ou vocal — et reçoit une réponse en
          moins de 30 secondes, 24h/24. Quand la demande dépasse le périmètre automatisé,
          la conversation est transmise à un humain avec un résumé clair.
        </p>
        <p className="body-text leading-relaxed mb-5">
          L'IA peut faire bien plus que répondre : elle peut consulter vos systèmes
          internes (CRM, agenda, comptabilité) pour donner une réponse personnalisée,
          créer un rendez-vous, modifier une commande, envoyer un PDF, prendre une
          réservation, transmettre un devis pré-calculé. Vous gardez le contrôle :
          chaque interaction est visible dans un tableau de bord, vous pouvez intervenir
          à tout moment, et les statistiques en temps réel vous montrent ce qui marche.
        </p>
        <p className="body-text leading-relaxed mb-5">
          Le déploiement se fait en 24 heures sur votre numéro WhatsApp Business
          existant. Le client n'a aucune application à installer, aucun compte à créer,
          rien à apprendre — il continue d'utiliser WhatsApp comme avant. Côté
          conformité, les données restent hébergées en Suisse, le chiffrement de bout
          en bout de WhatsApp est respecté, et toute l'architecture est compatible LPD
          et FADP.
        </p>
        <p className="body-text leading-relaxed">
          Le tarif est identique au réceptionniste vocal : CHF 900 de mise en place,
          CHF 200 par mois tout compris. Idéal pour les commerces, services à la
          personne, restaurants, salons, parkings, écoles, agences, et toute PME qui
          reçoit plus de 30 messages WhatsApp par semaine. Demandez une démo gratuite
          et testez votre futur chatbot WhatsApp en 15 minutes.
        </p>
      </SeoText>
    </Layout>
  );
};

export default WhatsApp;
